import * as THREE from 'three';
import { TilesRenderer } from '3d-tiles-renderer';
import {
    GoogleCloudAuthPlugin,
    ReorientationPlugin,
    GLTFExtensionsPlugin,
    TileFlatteningPlugin,
} from '3d-tiles-renderer/plugins';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { SITE_LAT, SITE_LON, SITE_HEIGHT_M, sceneScale } from './site';
import { device } from './deviceProfile';

// Google Photorealistic 3D Tiles under the masterplan: the real Diriyah
// plateau, Wadi Hanifah and the At-Turaif district instead of an empty
// backdrop. Ported from augmento-tower/src/three/googleTiles.ts.
//
// The tileset is reoriented so the site sits at the scene origin (Y-up, +Z
// north, +X west), the ground is snapped to y=0 by raycasting as higher-detail
// tiles stream in, and a flattening pad squashes whatever Google has on the
// plot so the masterplan gets a clean base.
//
// Scale: the tiles are metric and the model is a maquette, so the whole
// tileset is parented to a wrapper scaled by sceneScale.unitsPerMeter. Scaling
// the tiles down rather than the model up keeps every camera preset, hotspot
// radius and control distance in the app valid.

export const TILES_KEY: string | undefined = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as
    | string
    | undefined;

// Keep the first paint cheap: request a coarse neighbourhood, then tighten the
// detail target once the model has landed and the user is actually looking.
// Both come from the device profile; a phone gets coarser tiles throughout.
const PRELOAD_ERROR_TARGET = device.tilesPreloadError;
const INTERACTIVE_ERROR_TARGET = device.tilesInteractiveError;

// Brightness the unlit tile imagery is multiplied by at each end of the day.
// Measured against the lit masterplan, not picked for realism: at 1.0 the
// plateau sits about three stops under the model standing on it.
const DAY_GAIN = 1.7;
const NIGHT_GAIN = 0.32;

const DOWN = new THREE.Vector3(0, -1, 0);

// How far above and below the expected ground the levelling probe searches.
// Wide enough to find the terrain however badly the initial frame height was
// guessed, narrow enough that it cannot pick up the far side of the planet.
const PROBE_ABOVE_M = 4000;
const PROBE_BELOW_M = 6000;

export class GoogleTiles {
    readonly tiles: TilesRenderer;
    /** Scaled wrapper that holds the tileset; add this to the scene. */
    readonly group: THREE.Group;
    private flatten: TileFlatteningPlugin;
    private shapeMesh: THREE.Mesh;
    private shapeAdded = false;
    private snapTimer = 0;
    private groundLevel = 0;
    private lastReport = Number.NaN;
    /**
     * Called whenever the levelling moves the whole tileset.
     *
     * Anything seated on this terrain by raycast holds a world height that the
     * shift has just invalidated, so it has to sample again. Tiles stream for
     * tens of seconds on a cold cache, and the first correction regularly lands
     * after the desert town has already placed itself.
     */
    onLevelChanged: (() => void) | null = null;
    private settledPasses = 0;
    private padRadiusMeters: number;
    private raycaster = new THREE.Raycaster();
    private nightMats = new Set<THREE.MeshBasicMaterial>();
    private daylight = 1;
    private credit: HTMLDivElement;

    /**
     * @param padRadiusMeters Radius of the flattening pad that receives the
     *   masterplan; tiles content above it is pressed onto it.
     */
    constructor(
        apiToken: string,
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        padRadiusMeters: number,
    ) {
        const tiles = new TilesRenderer();
        tiles.registerPlugin(new GoogleCloudAuthPlugin({ apiToken, autoRefreshToken: true }));
        tiles.registerPlugin(
            new GLTFExtensionsPlugin({
                dracoLoader: new DRACOLoader().setDecoderPath(
                    'https://www.gstatic.com/draco/versioned/decoders/1.5.6/',
                ),
            }),
        );
        // azimuth 0 on purpose: it leaves the scene axes geo-aligned, which is
        // what lets the solar model put the sun in the right part of the sky.
        // The masterplan is rotated to match the plot instead (MODEL_HEADING_DEG).
        tiles.registerPlugin(
            new ReorientationPlugin({
                lat: THREE.MathUtils.degToRad(SITE_LAT),
                lon: THREE.MathUtils.degToRad(SITE_LON),
                height: SITE_HEIGHT_M,
                azimuth: 0,
            }),
        );
        this.flatten = new TileFlatteningPlugin();
        tiles.registerPlugin(this.flatten);

        tiles.setCamera(camera);
        tiles.setResolutionFromRenderer(camera, renderer);
        tiles.errorTarget = PRELOAD_ERROR_TARGET;
        // The cache defaults to holding 0.4 GB of tiles before it evicts
        // anything. A phone tab does not have 0.4 GB to spare on terrain with
        // a masterplan, five apartment models and a post chain in the same
        // context, and the browser kills the tab rather than reporting it.
        tiles.lruCache.maxBytesSize = device.tileCacheBytes;
        tiles.lruCache.minBytesSize = device.tileCacheBytes * 0.75;

        this.tiles = tiles;

        this.group = new THREE.Group();
        this.group.name = 'diriyah-context';
        this.group.scale.setScalar(sceneScale.unitsPerMeter);
        this.group.add(tiles.group);

        // Flattening pad, authored in metres in the tileset's own frame.
        this.padRadiusMeters = padRadiusMeters;
        this.shapeMesh = new THREE.Mesh(new THREE.CircleGeometry(padRadiusMeters, 64));
        this.shapeMesh.geometry.rotateX(-Math.PI / 2);

        // Photorealistic tiles are unlit daytime imagery: collect the materials
        // so the day/night controller can dim the city after dark.
        tiles.addEventListener('load-model', (e) => {
            const loaded = (e as unknown as { scene: THREE.Object3D }).scene;
            loaded.traverse((obj) => {
                const mesh = obj as THREE.Mesh;
                if (!mesh.isMesh) return;
                const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                for (const mat of mats) {
                    const basic = mat as THREE.MeshBasicMaterial;
                    this.nightMats.add(basic);
                    this.applyNight(basic);
                }
            });
        });
        tiles.addEventListener('dispose-model', (e) => {
            const removed = (e as unknown as { scene: THREE.Object3D }).scene;
            removed.traverse((obj) => {
                const mesh = obj as THREE.Mesh;
                if (!mesh.isMesh) return;
                const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                for (const mat of mats) this.nightMats.delete(mat as THREE.MeshBasicMaterial);
            });
        });

        // Required attribution for Google 3D Tiles.
        this.credit = document.createElement('div');
        this.credit.className = 'map-credit';
        this.credit.textContent = 'Map data ©2026 Google';
        document.body.appendChild(this.credit);
    }

    /**
     * Match the photoreal city to the scene's light (0 = night, 1 = full day).
     *
     * Google's tiles are unlit daytime imagery dropped into a scene that is
     * tone-mapped for a lit model, and they land noticeably darker than the
     * masterplan standing on them, which reads as the model floating over a
     * different photograph. So this is a full curve, not just a night dimmer:
     * the day end LIFTS the imagery into the model's range, the night end
     * takes it down without going black (Riyadh at night is lit, and a black
     * plateau under a lit masterplan reads as a hole rather than as darkness).
     */
    setDaylight(daylight: number): void {
        const next = THREE.MathUtils.clamp(daylight, 0, 1);
        if (Math.abs(next - this.daylight) < 0.01) return;
        this.daylight = next;
        for (const mat of this.nightMats) this.applyNight(mat);
    }

    /**
     * World Y that Google's terrain surface should be levelled to.
     *
     * This is the masterplan's own paving height, not zero. The maquette's base
     * group runs from well below the origin up to its paved courtyards, so
     * levelling the terrain to y=0 buries the plinth and leaves the buildings
     * standing several metres in the air. Aligning to the paving is what makes
     * the model sit ON the plateau.
     */
    setGroundLevel(worldY: number): void {
        if (this.groundLevel === worldY) return;
        this.groundLevel = worldY;
        this.snapTimer = 0;
    }

    /** World Y the terrain is levelled to; the anchor for any ground probe. */
    get groundLevelY(): number {
        return this.groundLevel;
    }

    setInteractive(interactive: boolean): void {
        this.tiles.errorTarget = interactive ? INTERACTIVE_ERROR_TARGET : PRELOAD_ERROR_TARGET;
    }

    set visible(value: boolean) {
        this.group.visible = value;
        this.credit.style.display = value ? '' : 'none';
    }

    get visible(): boolean {
        return this.group.visible;
    }

    private applyNight(mat: THREE.MeshBasicMaterial): void {
        mat.color?.setScalar(THREE.MathUtils.lerp(NIGHT_GAIN, DAY_GAIN, this.daylight));
    }

    update(dt: number): void {
        if (!this.group.visible) return;
        this.tiles.update();
        this.groundSnap(dt);
    }

    // Level the terrain so the natural grade beside the plot meets the model's
    // paving, then leave it alone.
    //
    // Two things here are deliberate and were both bugs before.
    //
    // The probe samples a RING outside the flattening pad, not the centre. The
    // pad presses the tiles onto itself, so a probe at the origin measures the
    // pad's own output: snap moves the frame, pad follows, next probe reads the
    // moved surface and snaps again. That loop is what made the masterplan sink
    // a little further every second.
    //
    // And it settles. Once the error is inside the deadband a couple of times
    // running it backs off to a slow watch and only re-levels for a big change
    // (a genuinely different LOD), instead of nudging the frame forever.
    private groundSnap(dt: number): void {
        this.snapTimer -= dt;
        if (this.snapTimer > 0) return;
        const settled = this.settledPasses >= 2;
        this.snapTimer = settled ? 10 : 2;

        // The probe window is anchored on the target ground level and made
        // generously tall, rather than derived from the tileset's bounds.
        //
        // Bounds are useless here: this is a planet-scale tileset, so its box is
        // the ECEF sphere and `max.y` sits about 6,372 km below the scene. A
        // probe started from that lands nowhere near Diriyah and every sample
        // misses, which is silent, the snap simply never corrects. A fixed
        // window either side of the expected grade always brackets the terrain.
        const scale = sceneScale.unitsPerMeter;
        const from = this.groundLevel + PROBE_ABOVE_M * scale;
        const far = (PROBE_ABOVE_M + PROBE_BELOW_M) * scale;
        const ringRadius = this.padRadiusMeters * 1.7 * scale;

        const heights: number[] = [];
        for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2;
            this.raycaster.set(
                new THREE.Vector3(Math.cos(a) * ringRadius, from, Math.sin(a) * ringRadius),
                DOWN,
            );
            this.raycaster.far = far;
            const hit = this.raycaster.intersectObject(this.group, true)[0];
            if (hit) heights.push(hit.point.y);
        }
        // Median, so one probe landing on a wadi wall or a rooftop cannot drag
        // the whole city with it.
        if (heights.length < 3) return;
        heights.sort((a, b) => a - b);
        const surface = heights[Math.floor(heights.length / 2)];

        const errorMeters = (surface - this.groundLevel) / scale;
        const deadband = settled ? 4 : 0.5;
        if (Math.abs(errorMeters) > deadband) {
            this.tiles.group.position.y -= errorMeters;
            this.group.updateMatrixWorld(true);
            this.syncFlattenShape();
            this.settledPasses = 0;
            this.onLevelChanged?.();
        } else {
            this.settledPasses++;
            if (!this.shapeAdded) this.syncFlattenShape();
        }

        const offset = Math.round(this.tiles.group.position.y * 10) / 10;
        if (offset !== this.lastReport) {
            this.lastReport = offset;
            console.info(
                `[tiles] levelled Diriyah terrain: grade was ${errorMeters.toFixed(1)} m out, ` +
                    `frame offset now ${offset.toFixed(1)} m`,
            );
        }
    }

    // Express the pad in the tileset's local frame, as TileFlatteningPlugin
    // expects, and (re)register it.
    //
    // The pad sits at the model's PAVING height, not at the scene origin. Pinned
    // to zero it ended up several metres below the levelled terrain and, with a
    // generous threshold, dragged everything within range down onto itself: the
    // masterplan sat at the bottom of a quarry with sheer sides.
    private syncFlattenShape(): void {
        this.tiles.group.updateMatrix();
        const local = this.tiles.group.matrix.clone().invert();
        const pad = new THREE.Matrix4()
            .makeTranslation(0, this.groundLevel / sceneScale.unitsPerMeter, 0)
            .premultiply(local);
        pad.decompose(this.shapeMesh.position, this.shapeMesh.quaternion, this.shapeMesh.scale);
        this.shapeMesh.updateMatrixWorld(true);
        const down = DOWN.clone().transformDirection(local).normalize();
        if (this.shapeAdded) {
            this.flatten.updateShape(this.shapeMesh);
        } else {
            this.shapeAdded = true;
            // The runtime takes an options object; the shipped .d.ts still
            // declares the deprecated numeric-threshold signature.
            //
            // Threshold is the reach of the flattening, and it is deliberately
            // modest: at 400 m it carved a crater out of the plateau instead of
            // seating a plot in it. This is enough to bed the site in and no more.
            this.flatten.addShape(this.shapeMesh, down, {
                thresholdMode: 'flatten',
                threshold: 45,
                flattenRange: 0,
            } as unknown as number);
        }
    }

    dispose(): void {
        this.credit.remove();
        this.tiles.dispose();
    }
}
